import React, { useState } from "react";
import { SbButton } from "uiCore";
import useAvailabilityStore from "Store/useAvailabilityStore";
import useRedeploymentPlanStore from "Store/useRedeploymentPlanStore";
import ShortlistConfirmModal from "../components/ShortlistConfirmModal";

// Employee View's people-first "Shortlist for Redeployment" / "Undo Shortlist"
// toggle — its own column on the outer people table (see
// useRedeploymentPeopleColumns) rather than living inside each person's
// expanded next-roles panel. Same store pair as
// RedeploymentReductionDetailV2's ShortlistAvailabilityButton, but this flow
// additionally walks the person's current role's Target Positions down by
// one on first mark (an employee leaving frees a seat) and gives it back on
// revert, which that target-first flow doesn't need — kept as its own
// component so that decrement/increment pairing stays obviously tied to this
// flow only.
//
// Every shortlist from here requires a Reason for Shortlisting (the shared
// ShortlistConfirmModal always asks for one) — the person leaves their current
// role's headcount on this screen, before any Reduction Detail ranking
// exists, so the reason is the only record of why. It's shown later in the
// role's Reduction Detail / View Planning "Reason" column.
const PersonAvailabilityButton = ({ person, size = "small" }) => {
  // Deliberately the store's raw "does a record exist" check
  // (hasAvailabilityRecord), not the active-availability rule
  // (isEmployeeAvailable/isActiveAvailabilityEntry) — same choice
  // ShortlistAvailabilityButton makes, and for the same reason: the
  // Candidate Profile page's per-role revert only drops one target role
  // (removeEmployeeTargetRole), so a person recommended for their last
  // targeted role and reverted there leaves behind an emptied-out record
  // (targeted_jd_sb_unique_ids: []) that isEmployeeAvailable would call "not
  // available" even though the store still holds stale data for this role.
  // This button offers "Revert recommendation" (and fully clears the record
  // on click) for as long as ANY record exists, not just an active one.
  const hasAvailabilityRecord = useRedeploymentPlanStore((state) =>
    state.hasAvailabilityRecord(person.currentRoleId, person.id)
  );
  const markAvailable = useAvailabilityStore((state) => state.markAvailable);
  const markUnavailable = useAvailabilityStore((state) => state.markUnavailable);
  const markEmployeeAvailable = useRedeploymentPlanStore((state) => state.markEmployeeAvailable);
  const unmarkEmployeeAvailable = useRedeploymentPlanStore((state) => state.unmarkEmployeeAvailable);
  const decrementTargetPosition = useRedeploymentPlanStore((state) => state.decrementTargetPosition);
  const incrementTargetPosition = useRedeploymentPlanStore((state) => state.incrementTargetPosition);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);

  const confirmMarkAvailable = ({ notifications, reason }) => {
    // People-first flow: an employee leaving frees exactly ONE seat, so only
    // their FIRST mark on this current role walks its Target Positions down —
    // marking the same person again from another path (a next role's profile)
    // must not take a second seat. Captured before the mark lands.
    const wasAlreadyAvailable = useRedeploymentPlanStore
      .getState()
      .isEmployeeAvailable(person.currentRoleId, person.id);
    markAvailable(person.id, "redeployment");
    // is_retained stays false: this people-first flow has no reduction
    // ranking to be "Retained" in — the reason is required here regardless.
    markEmployeeAvailable(person.currentRoleId, person.id, notifications, { isRetained: false, reason });
    if (!wasAlreadyAvailable) decrementTargetPosition(person.currentRoleId);
    setAvailabilityModalOpen(false);
  };

  // Undoes confirmMarkAvailable above — no confirm modal, since unlike
  // marking (which schedules notifications) there's nothing to configure on
  // the way back out. unmarkEmployeeAvailable always fully clears this
  // employee's entry for this current role (button shows for any record,
  // active or stale — see hasAvailabilityRecord above). The seat is only
  // given back when the record was still ACTIVE right before this clear —
  // if the Candidate Profile page's own per-role revert already returned it
  // (leaving exactly the stale, inactive record this button is now clearing),
  // incrementing again here would hand back a seat twice.
  const revertRecommendation = () => {
    const wasActive = useRedeploymentPlanStore.getState().isEmployeeAvailable(person.currentRoleId, person.id);
    markUnavailable(person.id);
    unmarkEmployeeAvailable(person.currentRoleId, person.id);
    if (wasActive) incrementTargetPosition(person.currentRoleId);
  };

  // The people row behind this button expands its next-roles panel on
  // click, and React bubbles clicks from the button AND from the portaled
  // modal (typing a reason, ticking a box, Cancel) up through the React tree
  // to that row. Stopping them at this root keeps shortlisting from also
  // expanding/collapsing the row.
  return (
    <span onClick={(e) => e.stopPropagation()}>
      {hasAvailabilityRecord ? (
        <SbButton buttonType="secondary" size={size} onClick={revertRecommendation}>
          Undo Shortlist
        </SbButton>
      ) : (
        <SbButton buttonType="primary" size={size} onClick={() => setAvailabilityModalOpen(true)}>
          Shortlist for Redeployment
        </SbButton>
      )}

      <ShortlistConfirmModal
        open={availabilityModalOpen}
        personName={person.name}
        onCancel={() => setAvailabilityModalOpen(false)}
        onConfirm={confirmMarkAvailable}
      />
    </span>
  );
};

export default PersonAvailabilityButton;
